// accordStandouts — lead with what sets a bottle apart from the rest of the shelf.
//
// Why (Rob, 2026-10-01): even with honest scoring, Amber reads on 59% of his
// shelf and Woody on 46%. A chip that half the shelf carries says nothing about
// this bottle. The accord that is rare *for this collector* goes first and is
// marked; a bottle with nothing rare is named as the core of the shelf instead.
//
// Pure functions over rows the collection page has already loaded: no I/O, no
// model calls, the same at 1 collector or 1,000,000.

import { deriveAccords, accordLabel } from './scentProfile.js'

// An accord is a standout when fewer than this share of the shelf carries it.
export const STANDOUT_RARE_SHARE = 0.25
// Below this many readable fragrances every accord is "rare", so say nothing.
export const STANDOUT_MIN_SHELF = 10

// Grouped cards and detail rows share name + house, so this identifies "the same scent on the shelf".
const memberKey = (f) => `${String(f?.name || '').trim().toLowerCase()}__${String(f?.house || '').trim().toLowerCase()}`

/** How many fragrances on the shelf carry each accord. `total` counts only rows we can read. */
export function shelfAccordProfile(rows) {
  const counts = {}
  const members = new Set()
  let total = 0
  for (const row of Array.isArray(rows) ? rows : []) {
    const accords = deriveAccords(row)
    if (!accords.length) continue
    total += 1
    members.add(memberKey(row))
    for (const a of accords) counts[a] = (counts[a] || 0) + 1
  }
  return { counts, total, members }
}

const eligible = (profile) => Boolean(profile && profile.total >= STANDOUT_MIN_SHELF)

/**
 * Chips for one fragrance against its shelf. The standout (the rarest accord
 * under the rare share) leads; the rest keep their strength order. Without an
 * eligible shelf this is plain deriveAccords output with no standout.
 */
export function accordStandoutView(fragrance, profile, max = 4) {
  const accords = deriveAccords(fragrance)
  const empty = { chips: [], standout: null, core: [] }
  if (!accords.length) return empty
  if (!eligible(profile)) {
    return { ...empty, chips: accords.slice(0, max).map((key) => ({ key, label: accordLabel(key), standout: false })) }
  }
  const onShelf = profile.members ? profile.members.has(memberKey(fragrance)) : true
  const share = (a) => (profile.counts[a] || 0) / profile.total
  const rare = accords
    .filter((a) => share(a) < STANDOUT_RARE_SHARE)
    .sort((a, b) => (profile.counts[a] || 0) - (profile.counts[b] || 0) || accords.indexOf(a) - accords.indexOf(b))
  const lead = rare[0] || null
  const ordered = lead ? [lead, ...accords.filter((a) => a !== lead)] : accords
  return {
    chips: ordered.slice(0, max).map((key) => ({ key, label: accordLabel(key), standout: key === lead })),
    // A finished or given-away bottle is not in the profile, so its count can be 0: keep it, never round up.
    standout: lead ? { key: lead, label: accordLabel(lead), count: profile.counts[lead] || 0, total: profile.total, onShelf } : null,
    core: lead ? [] : accords.map(accordLabel),
  }
}

const joinAnd = (labels) => (labels.length <= 1 ? labels.join('') : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`)

/** One plain sentence under the chips, or null when there is nothing true and useful to say. */
export function standoutSentence(view) {
  if (view?.standout) {
    const { label, count, total, onShelf = true } = view.standout
    if (count <= 0) return `None of the fragrances on your shelf right now read ${label}.`
    if (count === 1 && onShelf) return `This is the only fragrance on your shelf that reads ${label}.`
    if (count === 1) return `${label} is rare on your shelf: only 1 of your fragrances carries it.`
    // A small count reads as rare; past a handful, a ratio says it better than "only 58".
    if (count < 10 || !total) return `${label} is rare on your shelf: only ${count} of your fragrances carry it.`
    return `${label} sets this one apart: about 1 in ${Math.round(total / count)} of your fragrances carries it.`
  }
  if (view?.core?.length) {
    const core = view.core.slice(0, 2)
    return `Your signature lane: ${joinAnd(core)} ${core.length > 1 ? 'are' : 'is'} at the core of your shelf.`
  }
  return null
}

/**
 * The shelf's own signature: its most common accords with the share of readable
 * fragrances that carry each, strongest first. The same counts the standouts use,
 * so "Amber 60%" here and "Amber is at the core of your shelf" on a card agree.
 * Empty below STANDOUT_MIN_SHELF, where a percentage would mislead.
 */
export function shelfSignature(profile, max = 4) {
  if (!eligible(profile)) return []
  return Object.entries(profile.counts)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, max)
    .map(([key, count]) => ({ key, label: accordLabel(key), share: Math.round((100 * count) / profile.total) }))
}
